// ---------------------------------------------------------------------------
// History: the chat Copilot holds, as the thread shows it after a reload.
//
// `GET /history` answers `{messages: [...]}` laid out as the chat was live:
//   {role: 'user', text}                          a question
//   {role: 'user', approval_answer: 'yes'|'no'}   the answer to an approval card (worded here, in the user's language)
//   {role: 'assistant', text, steps?, approval?}  an answer; `steps` are `{id, name, label, status}`,
//                                                 `approval` is `{id, prompt, outcome}` with outcome approved | refused | moved_on | pending
// ---------------------------------------------------------------------------
import { parsePrompt } from './copilotApproval';

/** The thread messages for what Copilot sent; `t` words the user's yes or no. */
export function messagesFromHistory(history, t) {
  return (history?.messages ?? []).map((m) => {
    if (m.role === 'user') {
      if (m.approval_answer) return { role: 'user', content: m.approval_answer === 'yes' ? t('Approved') : t('Refused') };
      return { role: 'user', content: m.text ?? '' };
    }
    const custom = {};
    if (m.steps?.length) custom.steps = m.steps.map((s) => ({ id: s.id, name: s.name, label: s.label, status: s.status, secs: null }));
    if (m.approval) {
      const action = parsePrompt({ id: m.approval.id, prompt: m.approval.prompt });
      custom.approval = { id: action.id, actions: [action], outcome: m.approval.outcome };
    }
    return { role: 'assistant', content: m.text ?? '', ...(Object.keys(custom).length ? { metadata: { custom } } : {}) };
  });
}

const hasDetails = (messages) => messages.some((m) => m.metadata?.custom);

/** Whether Copilot's copy should replace the one this tab restored: more turns, a different (completed) last answer, or steps and cards the
 * tab's text-only copy cannot have. A copy that ends on a question is a cancelled turn. */
export function isNewerHistory(server, local) {
  const last = server[server.length - 1];
  if (!last || last.role !== 'assistant') return false;
  if (server.length !== local.length) return server.length > local.length;
  if (last.content !== local[local.length - 1].content) return true;
  return hasDetails(server) && !hasDetails(local);
}

/** A thread message as the tab keeps it for a reload: its text, and for an answer its steps and approval card, so they come back even for
 * turns Copilot's own copy of the chat no longer holds (it keeps only a window of the latest ones). */
export function storedMessageOf(message) {
  const base = { role: message.role, content: message.content?.find?.((p) => p.type === 'text')?.text ?? '' };
  const custom = message.role === 'assistant' ? message.metadata?.custom : null;
  if (!custom) return base;
  const kept = {};
  if (custom.steps?.length) {
    kept.steps = custom.steps.map((s) => ({
      id: s.id,
      name: s.name,
      label: s.label,
      status: s.status === 'running' ? 'done' : s.status,
      secs: s.secs ?? null,
      ...(s.kind ? { kind: s.kind, text: s.text ?? '' } : {}),
    }));
  }
  if (custom.approval) kept.approval = custom.approval;
  return Object.keys(kept).length ? { ...base, metadata: { custom: kept } } : base;
}
